"use client";

import {
  flexRender,
  getCoreRowModel,
  getPaginationRowModel,
  useReactTable,
  type ColumnDef,
} from "@tanstack/react-table";
import { ChevronLeft, ChevronRight } from "lucide-react";
import type { SelectList } from "../domain";
import { formatCell } from "./cell-renderers";
import { useTableLabelFn } from "./labels-context";
import { useTableComponents, useTableConfig } from "./table-provider";

interface ClientTableProps<TData> {
  columns: ColumnDef<TData>[];
  data: TData[];
  pageSize?: number;
  lists?: SelectList[];
  onRowClick?: (row: TData) => void;
  emptyMessage?: string;
}

export function ClientTable<TData>({
  columns,
  data,
  pageSize = 25,
  lists = [],
  onRowClick,
  emptyMessage,
}: ClientTableProps<TData>) {
  const t = useTableLabelFn();
  const { Button, EmptyState } = useTableComponents();
  const { locale } = useTableConfig();
  const table = useReactTable({
    columns,
    data,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    initialState: { pagination: { pageSize } },
  });

  const totalPages = table.getPageCount();
  const pageIndex = table.getState().pagination.pageIndex;
  const total = data.length;
  const startItem = total === 0 ? 0 : pageIndex * pageSize + 1;
  const endItem = Math.min((pageIndex + 1) * pageSize, total);

  if (data.length === 0) {
    return <EmptyState message={emptyMessage ?? t("clientTableEmpty")} />;
  }

  return (
    <div className="space-y-3">
      <div className="rounded-md border">
        <table className="w-full caption-bottom text-sm">
          <thead className="[&_tr]:border-b">
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id} className="border-b transition-colors">
                {headerGroup.headers.map((header) => (
                  <th
                    key={header.id}
                    className="h-12 px-4 text-left align-middle font-medium text-text-secondary"
                  >
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                          header.column.columnDef.header,
                          header.getContext(),
                        )}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody>
            {table.getRowModel().rows.map((row) => (
              <tr
                key={row.id}
                className="border-b transition-colors hover:bg-surface/50 cursor-pointer"
                onClick={() => onRowClick?.(row.original)}
              >
                {row.getVisibleCells().map((cell) => (
                  <td key={cell.id} className="p-4 align-middle">
                    {(() => {
                      const meta = cell.column.columnDef.meta;
                      const fmt = meta?.format;
                      if (fmt) {
                        const colList = lists.find(
                          (l) => l.propertyKey === cell.column.id,
                        );
                        return formatCell(
                          cell.getValue(),
                          fmt,
                          meta?.formatOptions,
                          colList?.items,
                          locale,
                        );
                      }
                      if (cell.column.columnDef.cell) {
                        return flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext(),
                        );
                      }
                      return String(cell.getValue() ?? "—");
                    })()}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <div className="text-sm text-text-secondary">
            {t("clientTableShowing", { from: startItem, to: endItem, total })}
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="icon"
              disabled={!table.getCanPreviousPage()}
              onClick={() => table.previousPage()}
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <span className="text-sm">
              {pageIndex + 1} / {totalPages}
            </span>
            <Button
              variant="outline"
              size="icon"
              disabled={!table.getCanNextPage()}
              onClick={() => table.nextPage()}
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
